import { useMemo, useState } from "react";
import { ClipboardCheck, Eye, FileText, Map, PanelRightClose, ReceiptText, Ruler, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import type { WSFile } from "@/hooks/useBridgetteWorkspace";
import { DocumentPreviewDialog } from "./DocumentPreviewDialog";
import { ScopeReviewCard, type ScopeReviewAttachment } from "./ScopeReviewCard";

interface Props {
  files: WSFile[];
  selectedFile?: WSFile | null;
  onQuickAction: (prompt: string) => void;
  disabled?: boolean;
  onToggleCollapse?: () => void;
  embedded?: boolean;
}

const countLineItems = (value: unknown) => Array.isArray(value) ? value.length : 0;
const countFlags = (value: unknown) => Array.isArray(value) ? value.length : 0;

export function WorkspaceReviewCanvas({ files, selectedFile, onQuickAction, disabled = false, onToggleCollapse, embedded = false }: Props) {
  const [previewOpen, setPreviewOpen] = useState(false);

  const attachment = useMemo<ScopeReviewAttachment | null>(() => {
    if (!selectedFile) return null;
    return {
      type: "bridgette_scope_review",
      fileName: selectedFile.file_name,
      category: selectedFile.category,
      summary: selectedFile.vision_summary,
      extracted: selectedFile.extracted_data ?? {},
    };
  }, [selectedFile]);

  const extracted = selectedFile?.extracted_data ?? {};
  const measurementFile = useMemo(
    () => files.find((file) => file.category === "measurement_report") ?? null,
    [files],
  );
  const measurementExtracted = measurementFile?.extracted_data ?? {};
  const lineItemCount = countLineItems(extracted.line_items);
  const flagCount = countFlags(extracted.document_flags);
  const recommendationCount = countLineItems(extracted.supplement_recommendations);
  const blueprintAnalysis = extracted.blueprint_analysis ?? {};
  const blueprintDimensionCount = countLineItems(blueprintAnalysis.dimensions);
  const blueprintTakeoffCount = countLineItems(blueprintAnalysis.takeoff_items);
  const blueprintTradeCount = countLineItems(blueprintAnalysis.trade_scopes ?? extracted.trade_breakdown);
  const blueprintPricedCount = typeof extracted.pricing_summary?.priced_items_count === "number"
    ? extracted.pricing_summary.priced_items_count
    : typeof blueprintAnalysis.pricing_summary?.priced_items_count === "number"
      ? blueprintAnalysis.pricing_summary.priced_items_count
      : 0;
  const isBlueprint = selectedFile?.category === "blueprint" || extracted.document_type === "blueprint";

  return (
    <aside className={embedded ? "relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-muted/10" : "relative flex h-full min-h-0 min-w-0 flex-col overflow-hidden border-l border-border bg-muted/10"}>
      <div className="pointer-events-none absolute inset-x-8 top-6 h-24 rounded-full bg-primary/10 blur-3xl" />
      <header className="relative z-10 border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className="gap-1 border-primary/30 bg-primary/5 text-primary shadow-sm">
                <ClipboardCheck className="h-3 w-3" /> Review canvas
              </Badge>
              {selectedFile?.category ? <Badge variant="secondary">{selectedFile.category}</Badge> : null}
            </div>
            <h1 className="truncate text-base font-semibold text-foreground">
              {selectedFile ? selectedFile.file_name : "Attach documents to start review"}
            </h1>
            <p className="mt-1 max-w-xl text-xs text-muted-foreground">
              {selectedFile
                ? isBlueprint
                  ? "Bridgette is running a blueprint pass for rotated callouts, tiny labels, dimensions, schedules, notes, and takeoff-ready items."
                  : "Bridgette is turning this document into line items, quantity checks, flags, and supplement leads you can review."
                : "Attach a document and Bridgette will turn it into line items, quantity checks, flags, and supplement leads you can review."}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            <Button type="button" variant="outline" size="sm" className="rounded-lg border-border bg-card shadow-sm hover:bg-muted/50" disabled={!selectedFile} onClick={() => setPreviewOpen(true)}>
              <Eye className="mr-2 h-4 w-4" /> Preview document
            </Button>
            <Button type="button" variant="outline" size="icon" className="rounded-lg border-border bg-card shadow-sm hover:bg-muted/50" onClick={onToggleCollapse}>
              <PanelRightClose className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <section className="relative z-10 grid grid-cols-2 gap-3 border-b border-border bg-background/60 px-4 py-3">
        <Card className="min-w-0 animate-fade-in rounded-lg border-border bg-card p-3 shadow-sm">
          <div className="flex min-w-0 items-center gap-2 text-[11px] font-medium text-muted-foreground"><ReceiptText className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">{isBlueprint ? "Scope items" : "Lines"}</span></div>
          <div className="mt-2 text-xl font-semibold text-foreground">{isBlueprint ? Math.max(lineItemCount, blueprintTakeoffCount) : lineItemCount}</div>
        </Card>
        <Card className="min-w-0 animate-fade-in rounded-lg border-border bg-card p-3 shadow-sm [animation-delay:60ms]">
          <div className="flex min-w-0 items-center gap-2 text-[11px] font-medium text-muted-foreground"><Ruler className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">{isBlueprint ? "Trades" : "Measurements"}</span></div>
          <div className="mt-2 text-sm font-semibold text-foreground">{isBlueprint ? blueprintTradeCount : measurementFile ? "Report loaded" : "Awaiting report"}</div>
          <div className="mt-1 break-words text-[11px] leading-5 text-muted-foreground">
            {isBlueprint ? "Trade-by-trade scope grouped from the plan set." : measurementFile ? measurementFile.file_name : "Upload a measurement report here to compare quantities."}
          </div>
        </Card>
        <Card className="min-w-0 animate-fade-in rounded-lg border-border bg-card p-3 shadow-sm [animation-delay:80ms]">
          <div className="flex min-w-0 items-center gap-2 text-[11px] font-medium text-muted-foreground">{isBlueprint ? <Map className="h-4 w-4 shrink-0 text-primary" /> : <FileText className="h-4 w-4 shrink-0 text-primary" />} <span className="truncate">{isBlueprint ? "Dimensions" : "Flags"}</span></div>
          <div className="mt-2 text-xl font-semibold text-foreground">{isBlueprint ? blueprintDimensionCount : flagCount}</div>
        </Card>
        <Card className="min-w-0 animate-fade-in rounded-lg border-border bg-card p-3 shadow-sm [animation-delay:160ms]">
          <div className="flex min-w-0 items-center gap-2 text-[11px] font-medium text-muted-foreground"><Wand2 className="h-4 w-4 shrink-0 text-primary" /> <span className="truncate">{isBlueprint ? "Priced rows" : "Opportunities"}</span></div>
          <div className="mt-2 text-xl font-semibold text-foreground">{isBlueprint ? blueprintPricedCount : recommendationCount}</div>
        </Card>
      </section>

      <div className="relative z-10 flex-1 overflow-y-auto px-4 py-4">
        {!selectedFile ? (
          <div className="flex h-full min-h-[420px] items-center justify-center text-center">
            <div className="max-w-md animate-enter">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg border border-border bg-card shadow-sm">
                <ClipboardCheck className="h-7 w-7 text-primary" />
              </div>
              <h2 className="text-lg font-semibold text-foreground">This is Bridgette’s review area.</h2>
              <p className="mt-2 text-sm text-muted-foreground">Once you attach a file, this panel shows the clean breakdown: extracted lines, quantity checks, missing items, flags, and the draft-ready review Bridgette builds from the document.</p>
            </div>
          </div>
        ) : attachment ? (
          <div className="animate-fade-in">
            <ScopeReviewCard
              attachment={attachment}
              measurementFile={measurementFile}
              measurementExtracted={measurementExtracted}
              onQuickAction={onQuickAction}
              disabled={disabled}
            />
          </div>
        ) : null}
      </div>

      <DocumentPreviewDialog file={selectedFile ?? null} open={previewOpen && Boolean(selectedFile)} onOpenChange={setPreviewOpen} />
    </aside>
  );
}
